import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Switch,
  VisuallyHidden,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger,
} from "@/components/ui/Drawer";
import {
  FormErrorDisplay,
  RootErrorDisplay,
} from "@/components/ui/FormErrorDisplay";
import DateInput from "@/components/ui/DateInput";
import SearchableSelect, { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import withHashDrawer from "@/components/ui/withHashDrawer";
import {
  UserCreateRequest,
  UserUpdateRequest,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUserByIdQuery,
} from "../api";
import { useLazyGetOrganizationsQuery, Organization } from "@/entities/organizations/api";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";
import { isFlagOn } from "../flags";

interface UserFormData {
  recovery_mobile_phone: string;
  email: string;
  first_name: string;
  last_name: string;
  organization_id: number;
  email_verified: boolean;
  email_verified_at: string | null;
  active: boolean;
}

// Schema definition
const userSchema: yup.ObjectSchema<UserFormData> = yup.object({
  recovery_mobile_phone: yup.string().default("").test("phone", "Enter a valid mobile phone number including its country code", value => !value || /^\+[1-9][0-9]{6,14}$/.test(value.replace(/[\s().-]+/g, ""))),
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  first_name: yup
    .string()
    .required("First name is required")
    .min(1, "First name cannot be empty")
    .max(50, "First name must be less than 50 characters"),
  last_name: yup
    .string()
    .required("Last name is required")
    .min(1, "Last name cannot be empty")
    .max(50, "Last name must be less than 50 characters"),
  organization_id: yup
    .number()
    .required("Organization is required")
    .positive("Please select an organization"),
  email_verified: yup.boolean().default(false),
  active: yup.boolean().default(true),
  email_verified_at: yup
    .string()
    .nullable()
    .default(null)
    .when("email_verified", {
      is: true,
      then: (schema) => schema.required("Verification date is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

interface UserCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of user to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

const UserCreateEditModal: React.FC<UserCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { data: editingEntity } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });

  const isEditing = Boolean(entityId);
  
  // API hooks
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: yupResolver(userSchema),
    defaultValues: {
      recovery_mobile_phone: "",
      email: "",
      first_name: "",
      last_name: "",
      organization_id: 0,
      email_verified: false,
      email_verified_at: null,
      active: true,
    },
  });

  // Error handling
  const { handleSubmissionError } = useFormErrorHandler(setError, clearErrors);

  // Organization search state
  const [selectedOrganization, setSelectedOrganization] = useState<SearchableSelectOption | null>(null);

  // Use lazy query for organization search
  const [triggerOrgSearch] = useLazyGetOrganizationsQuery();

  const isLoading = isCreating || isUpdating;
  const isEmailVerified = watch("email_verified");

  // Organization search function using lazy RTK Query
  const searchOrganizations = useCallback(async (searchQuery: string): Promise<SearchableSelectOption[]> => {
    try {
      const result = await triggerOrgSearch({
        search: searchQuery.trim() || undefined,
        per_page: 50,
        page: 1,
      }).unwrap();
      
      return result.data.map((org: Organization) => ({
        value: org.id.toString(),
        label: org.name,
      }));
    } catch (error) {
      console.error('Organization search failed:', error);
      return [];
    }
  }, [triggerOrgSearch]);

  // Handle modal close with form reset
  const handleClose = () => {
    reset({
      recovery_mobile_phone: "",
      email: "",
      first_name: "",
      last_name: "",
      organization_id: 0,
      email_verified: false,
      email_verified_at: null,
      active: true,
    });
    setSelectedOrganization(null);
    onClose();
  };

  // Reset form when modal opens or editingEntity changes
  useEffect(() => {
    if (isOpen && entityId && editingEntity && editingEntity.id === entityId) {
      const existingEmailVerifiedAt = editingEntity.email_verified_at || null;
      const isVerified = Boolean(existingEmailVerifiedAt);
      reset({
        recovery_mobile_phone: editingEntity.recovery_mobile_phone || "",
        email: editingEntity.email,
        first_name: editingEntity.first_name,
        last_name: editingEntity.last_name,
        organization_id: editingEntity.organization?.id || 0,
        email_verified: isVerified,
        email_verified_at: isVerified ? (existingEmailVerifiedAt || new Date().toISOString()) : null,
        active: isFlagOn(editingEntity.active),
      });
      
      // Set selected organization for SearchableSelect
      if (editingEntity.organization) {
        setSelectedOrganization({
          value: editingEntity.organization.id.toString(),
          label: editingEntity.organization.name,
        });
      } else {
        setSelectedOrganization(null);
      }
    } else if (isOpen && !entityId) {
      // New mode - reset to empty values
      reset({
        recovery_mobile_phone: "",
        email: "",
        first_name: "",
        last_name: "",
        organization_id: 0,
        email_verified: false,
        email_verified_at: null,
        active: true,
      });
      setSelectedOrganization(null);
    }
  }, [entityId, editingEntity, isOpen, reset]);

  const onSubmit = async (data: UserFormData) => {
    try {
      if (isEditing && !editingEntity) {
        return;
      }

      if (isEditing && editingEntity) {
        const updateData: UserUpdateRequest = {
          first_name: data.first_name,
          last_name: data.last_name,
          active: data.active,
          status: editingEntity.status,
          organization_id: data.organization_id,
          email_verified_at: data.email_verified
            ? (data.email_verified_at || editingEntity.email_verified_at || new Date().toISOString())
            : null,
        };
        if (data.recovery_mobile_phone !== (editingEntity.recovery_mobile_phone || "")) {
          updateData.recovery_mobile_phone = data.recovery_mobile_phone.trim() || null;
        }
        await updateUser({ id: editingEntity.id, ...updateData }).unwrap();
      } else {
        const createData: UserCreateRequest = {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          active: true,
          status: 'active',
          roles: ['user'],
          organization_id: data.organization_id,
        };
        await createUser(createData).unwrap();
      }
      onSuccess?.();
      handleClose();
    } catch (error) {
      handleSubmissionError(error);
    }
  };



  // This will be called from SearchableSelect when an option is actually selected
  const handleOrganizationSelect = useCallback((option: SearchableSelectOption) => {
    setSelectedOrganization(option);
    setValue('organization_id', Number(option.value));
    clearErrors('organization_id');
  }, [setValue, clearErrors]);

  const handleEmailVerifiedChange = useCallback((checked: boolean) => {
    setValue("email_verified", checked);
    if (checked) {
      const existingDate = getValues("email_verified_at");
      if (!existingDate) {
        setValue("email_verified_at", new Date().toISOString());
      }
      clearErrors("email_verified_at");
      return;
    }
    setValue("email_verified_at", null);
    clearErrors("email_verified_at");
  }, [clearErrors, getValues, setValue]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>{isEditing ? 'Edit User' : 'Create New User'}</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DrawerBody>
          <VStack gap={4} align="stretch">
            <RootErrorDisplay />
            
            <Box>
              <Text fontWeight="medium" mb={1}>
                Email {!isEditing && <Text as="span" color="red.500">*</Text>}
              </Text>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="user@example.com"
                    disabled={isEditing || isLoading}
                  />
                )}
              />
              <FormErrorDisplay error={errors.email} />
            </Box>

            {isEditing && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">
                    Email Verification Date
                  </Text>
                  <Controller
                    name="email_verified"
                    control={control}
                    render={({ field }) => (
                      <Switch.Root
                        checked={Boolean(field.value)}
                        onCheckedChange={(details) => handleEmailVerifiedChange(details.checked)}
                        disabled={isLoading}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control />
                        <Switch.Label>{field.value ? "Verified" : "Not verified"}</Switch.Label>
                      </Switch.Root>
                    )}
                  />
                </HStack>
                <Controller
                  name="email_verified_at"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      disabled={isLoading || !isEmailVerified}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.email_verified_at} />
              </Box>
            )}

            {isEditing && (
              <Box>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Account</Text>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch.Root
                        checked={Boolean(field.value)}
                        onCheckedChange={(details) => field.onChange(details.checked)}
                        disabled={isLoading}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control />
                        <Switch.Label><VisuallyHidden>User account </VisuallyHidden>{field.value ? "Active" : "Inactive"}</Switch.Label>
                      </Switch.Root>
                    )}
                  />
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Only active doctors are published to the Clarity chatbot catalog.
                </Text>
              </Box>
            )}

            {isEditing && (
              <Box>
                <Text fontWeight="medium" mb={1}>Recovery mobile phone number</Text>
                <Controller
                  name="recovery_mobile_phone"
                  control={control}
                  render={({ field }) => <Input {...field} type="tel" placeholder="+1 (415) 555-2671" disabled={isLoading} />}
                />
                <FormErrorDisplay error={errors.recovery_mobile_phone} />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Saving a new number marks it verified without SMS. Include the country code. Leave blank to remove it.
                </Text>
              </Box>
            )}

            <HStack gap={4}>
              <Box flex={1}>
                <Text fontWeight="medium" mb={1}>
                  First Name <Text as="span" color="red.500">*</Text>
                </Text>
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="John"
                      disabled={isLoading}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.first_name} />
              </Box>

              <Box flex={1}>
                <Text fontWeight="medium" mb={1}>
                  Last Name <Text as="span" color="red.500">*</Text>
                </Text>
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Doe"
                      disabled={isLoading}
                    />
                  )}
                />
                <FormErrorDisplay error={errors.last_name} />
              </Box>
            </HStack>

            <Box>
              <Text fontWeight="medium" mb={1}>
                Organization <Text as="span" color="red.500">*</Text>
              </Text>
              <SearchableSelect
                key={selectedOrganization?.value || 'empty'}
                value={selectedOrganization?.value || ""}
                onOptionSelect={handleOrganizationSelect}
                onSearch={searchOrganizations}
                placeholder="Search and select organization..."
                disabled={isLoading}
                selectedOption={selectedOrganization}
                minSearchLength={1}
                debounceMs={300}
                loadingText="Searching organizations..."
                noResultsText="No organizations found"
              />
              <FormErrorDisplay error={errors.organization_id} />
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack gap={3}>
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading || (isEditing && !editingEntity)}
            >
              {isEditing ? 'Update User' : 'Create User'}
            </Button>
          </HStack>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const UserCreateModal = withHashDrawer(UserCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const UserEditModal = withHashDrawer(UserCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default UserCreateModal; 
